import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { track } from "../lib/mixpanel"
import { burnChartConfig, changeOrdersChartConfig, healthChartConfig } from "../lib/portfolio_trend_chart_configs"

const STORAGE_KEY = "portfolio.trends.expanded"

// Renders the three portfolio trend charts (burn rate / CO trend / health)
// inside a collapsible section below the insights row. Charts init lazily
// on first expand — Chart.js needs a visible, sized canvas to compute
// dimensions. Expand state persists across turbo-frame re-renders via
// localStorage so filter changes don't re-collapse the section.
export default class extends Controller {
  static targets = ["toggle", "chevron", "body", "burnCanvas", "coCanvas", "healthCanvas"]
  static values = { series: Object }

  connect() {
    this._charts = []
    this._initialized = false
    this._viewedFired = false

    if (window.localStorage.getItem(STORAGE_KEY) === "1") {
      this._expand({ fromUser: false })
    }
  }

  disconnect() {
    this._destroyCharts()
  }

  toggle(event) {
    event.preventDefault()
    const collapsed = this.bodyTarget.classList.contains("hidden")
    if (collapsed) this._expand({ fromUser: true })
    else this._collapse()
  }

  _expand({ fromUser }) {
    this.bodyTarget.classList.remove("hidden")
    this.chevronTarget.classList.add("rotate-180")
    this.toggleTarget.setAttribute("aria-expanded", "true")
    window.localStorage.setItem(STORAGE_KEY, "1")
    this._initCharts()
    if (fromUser && !this._viewedFired) this._fireViewedEvent()
  }

  _collapse() {
    this.bodyTarget.classList.add("hidden")
    this.chevronTarget.classList.remove("rotate-180")
    this.toggleTarget.setAttribute("aria-expanded", "false")
    window.localStorage.setItem(STORAGE_KEY, "0")
  }

  _initCharts() {
    if (this._initialized) return
    this._initialized = true
    const s = this.seriesValue

    if (this.hasBurnCanvasTarget) this._charts.push(this._buildBurn(s))
    if (this.hasCoCanvasTarget) this._charts.push(this._buildCO(s))
    if (this.hasHealthCanvasTarget && s.health_has_history) {
      this._charts.push(this._buildHealth(s))
    }
  }

  _destroyCharts() {
    this._charts.forEach(c => c?.destroy?.())
    this._charts = []
    this._initialized = false
  }

  _fireViewedEvent() {
    this._viewedFired = true
    const s = this.seriesValue || {}
    track("Portfolio Charts Viewed", {
      snapshot_month_count: (s.health?.green || []).filter(v => v !== null && v !== undefined).length,
      health_has_history: !!s.health_has_history
    })
  }

  // ── Chart builders ─────────────────────────────────────────
  // Configs live in lib/portfolio_trend_chart_configs so the Ask AI
  // standalone trend cards render pixel-identical charts.

  _buildBurn(s) {
    return new Chart(this.burnCanvasTarget, burnChartConfig(s))
  }

  _buildCO(s) {
    return new Chart(this.coCanvasTarget, changeOrdersChartConfig(s))
  }

  _buildHealth(s) {
    return new Chart(this.healthCanvasTarget, healthChartConfig(s))
  }

}
